import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment-timezone';
import { SingleDatePicker, SingleDatePickerShape } from 'react-dates';
import classNames from 'classnames';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import * as ShopSelectors from 'selectors/ShopSelectors';
import { localFormatString } from 'common/utils/dateUtils';

interface Props {
	value: string | moment.Moment | undefined;
	onChange: (value: moment.Moment | null) => void;
	id: string;
	styleProps?: {
		height?: number;
		bordered?: boolean;
	};
	singleDatePickerProps?: Partial<SingleDatePickerShape>;
	disabled?: boolean;
}

const DatePicker = (props: Props) => {
	const [isOpen, setIsOpen] = useState(false);
	const classes = useStyles(props);
	const startDateNumber = useSelector(ShopSelectors.getStartDateNumber);
	const shopDateFormat = useSelector(ShopSelectors.getShopDateFormat);
	const dateFormatString = localFormatString('ddd DD.MM.', shopDateFormat);
	return (
		<div
			className={classNames(classes.root, {
				[classes.bordered]: props.styleProps?.bordered,
			})}
		>
			<SingleDatePicker
				date={moment(props.value, 'YYYY-MM-DD')}
				onDateChange={props.onChange}
				focused={isOpen}
				onFocusChange={({ focused }) => {
					if (focused != null) {
						setIsOpen(focused);
					}
				}}
				id={props.id}
				numberOfMonths={1}
				firstDayOfWeek={startDateNumber}
				displayFormat={dateFormatString}
				noBorder
				readOnly
				disabled={props.disabled}
				{...props.singleDatePickerProps}
			/>
		</div>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		root: {
			'@global': {
				'.SingleDatePicker': {
					width: '100%',
					fontFamily: theme.typography.fontFamily,
				},
				'.SingleDatePickerInput': {
					display: 'flex',
					margin: 1,
				},
				'.SingleDatePickerInput_calendarIcon': {
					margin: 0,
				},
				'.DateInput': {
					width: '100%',
				},
				'.DateInput_input': {
					border: 'none',
					padding: 10,
					fontSize: '1.6rem',
					fontFamily: theme.typography.fontFamily,
					height: (props: Props) => props.styleProps?.height,
					fontWeight: 500,
					color: theme.palette.text.primary,
				},
				'.DateInput_input__focused': {
					border: 'none',
				},
			},
		},
		bordered: {
			border: '1px solid rgba(0, 0, 0, 0.23)',
			borderRadius: 4,
		},
	}),
);

export default DatePicker;
